import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { HStack } from "@whoosh/design-system/src/components/primitive";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";

import { EventDetailSeriesSessionsModal } from "../EventDetailSeriesSessionsModal/EventDetailSeriesSessionsModal";
import { EventDetailSeriesNavButtons_event$key } from "./__generated__/EventDetailSeriesNavButtons_event.graphql";

type EventDetailSeriesNavButtonsProps = {
  fragmentRef: EventDetailSeriesNavButtons_event$key | null;
};

export const EventDetailSeriesNavButtons = ({
  fragmentRef,
}: EventDetailSeriesNavButtonsProps) => {
  const data = useFragment(EventDetailSeriesNavButtonsFragment, fragmentRef);
  const [modalType, setModalType] = useState<"past" | "upcoming" | null>(null);

  const eventSeries = data?.eventSeries;
  const eventNounSingular = getNounOverride(
    eventSeries?.facility?.club?.copyOverrides?.event?.singular,
    t`Event`
  );
  const eventNounPlural = getNounOverride(
    eventSeries?.facility?.club?.copyOverrides?.event?.plural,
    t`Events`
  );
  const id = data?.id;

  const otherEventNodes = eventSeries?.events?.edges
    ?.map((edge) => edge?.node ?? null)
    .filter((n) => n?.id !== id);

  if (!otherEventNodes?.length) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pastEvents =
    otherEventNodes?.filter(
      (node) => node?.endOn && new Date(node.endOn) < today
    ) ?? [];

  const upcomingEvents =
    otherEventNodes?.filter(
      (node) => !node?.endOn || new Date(node.endOn) >= today
    ) ?? [];

  const modalEvents = modalType === "past" ? pastEvents : upcomingEvents;
  const modalTitle =
    modalType === "past"
      ? t`${eventNounSingular} Past Sessions`
      : t`${eventNounSingular} Upcoming Sessions`;

  return (
    <>
      <HStack gap={2} flexWrap="wrap" testID="event-detail-series-nav-buttons">
        {pastEvents.length > 0 ? (
          <Button
            variant="outline"
            size="tiny"
            onPress={() => setModalType("past")}
            accessibilityLabel={t`View Past ${eventNounPlural} in the Series`}
            label={t`View Past ${eventNounPlural} in the Series`}
          />
        ) : null}
        {upcomingEvents.length > 0 ? (
          <Button
            variant="outline"
            size="tiny"
            onPress={() => setModalType("upcoming")}
            accessibilityLabel={t`View Upcoming ${eventNounPlural} in the Series`}
            label={t`View Upcoming ${eventNounPlural} in the Series`}
          />
        ) : null}
      </HStack>

      <EventDetailSeriesSessionsModal
        isOpen={modalType !== null}
        onClose={() => setModalType(null)}
        title={modalTitle}
        events={modalEvents}
      />
    </>
  );
};

export const EventDetailSeriesNavButtonsFragment = graphql`
  fragment EventDetailSeriesNavButtons_event on Event {
    id

    eventSeries {
      facility {
        club {
          copyOverrides {
            event {
              singular
              plural
            }
          }
        }
      }
      events {
        edges {
          node {
            id
            endOn
            ...EventCardWrapper_Event
          }
        }
      }
    }
  }
`;
